import { useQueryClient } from "@tanstack/react-query";
import {
  Outlet,
  createFileRoute,
  redirect,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { NavBar } from "@/components/chrome/nav-bar";
import type { NavRole } from "@/components/chrome/nav-links";
import { SearchProvider } from "@/components/search/search-provider";
import { isAuthed, pb, signOut } from "@/lib/pb";
import { DEFAULT_BRAND, type TenantBrand, resolveTenantBrand } from "@/lib/tenant-brand";

/**
 * App shell — fixed glassmorphic NavBar (ported from portal-ui's nav-bar)
 * + SchoolBackground drifting subject icons + max-w-7xl main column with
 * pt-20 offset so content clears the fixed bar.
 */
export const Route = createFileRoute("/_app")({
  beforeLoad: ({ location }) => {
    if (!isAuthed()) {
      throw redirect({ to: "/signin", search: { next: location.href } });
    }
  },
  component: AppShell,
});

/**
 * Pages loaded with `?embed=1` skip the NavBar and drop the fixed-bar
 * padding. Used by the support /components catalog so its iframes show the
 * page content alone, without the redundant navbar / brand / sign-out menu.
 */
function isEmbedMode(): boolean {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).get("embed") === "1";
}

// Routes that should escape the default `max-w-7xl` main column and use the
// full viewport. The /components catalog needs the breathing room to show
// many sections side-by-side with minimal horizontal scroll. Other routes
// keep the centred container so they read like real pages.
const FULL_WIDTH_ROUTES = new Set<string>(["/components"]);

function AppShell() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const role = ((pb.authStore.model as { role?: string } | null)?.role ?? "student") as NavRole;
  const embed = isEmbedMode();
  const pathname = useLocation({ select: (s) => s.pathname });
  const fullWidth = FULL_WIDTH_ROUTES.has(pathname);

  const [brand, setBrand] = useState<TenantBrand>(DEFAULT_BRAND);
  useEffect(() => {
    setBrand(resolveTenantBrand(window.location.host));
  }, []);

  async function onSignOut() {
    await signOut();
    // Reset every cached query — otherwise the next user to sign in on this
    // browser would briefly see the previous user's data (RBAC-allowed for
    // them, not for the new user). Defense-in-depth: query keys also embed
    // the auth.id, so even a missed clear can't cross-pollinate caches.
    queryClient.clear();
    toast.success("Signed out");
    navigate({ to: "/signin" });
  }

  return (
    <SearchProvider>
      <div className="text-foreground flex min-h-screen flex-col">
        {!embed && <NavBar role={role} brand={brand} onSignOut={onSignOut} />}
        <main
          className={`w-full flex-1 px-4 md:px-6 ${
            embed ? "py-4" : "pb-10 pt-20 md:pt-24"
          } ${fullWidth ? "" : "mx-auto max-w-7xl"}`}
        >
          <Outlet />
        </main>
      </div>
    </SearchProvider>
  );
}
